import { Panel, Typography } from "@/components/atoms";
import { PageList } from "@/components/molecules";
import { usePermissions } from "@/permissions";
import { EPermission } from "@/permissions/types";
import { BillingSettings } from "./BillingSettings/BillingSettings";
import { CancellationFees } from "./CancellationFees/CancellationFees";
import { DispatchPanel } from "./DispatchPanel";
import { FixedCostExtras } from "./FixedCostExtras/FixedCostExtras";
import { HourlyDailyHire } from "./HourlyDailyHire/HourlyDailyHire";
import { TimeAndDay } from "./TimeAndDay/TimeAndDay";
import { TransferTypes } from "./TransferTypes/TransferTypes";
import { TripCost } from "./TripCost/TripCost";
import { TripCost as TripCostV2 } from "./TripCostV2/TripCost";
import { UnscheduledStops } from "./UnscheduledStops/UnscheduledStops";
import { VehicleTypes } from "./VehicleTypes/VehicleTypes";
import { WaitingTimes } from "./WaitingTimes/WaitingTimes";

type Subpage = {
  name: string;
  desc: string;
  to: string;
  icon: string;
  permissions: EPermission[];
};

const subpages: Subpage[] = [
  {
    name: "Trip Cost",
    desc: "Standard trip pricing configuration",
    to: "trip-cost",
    icon: "DollarCircle",
    permissions: [EPermission.ProviderConfigPricingManage, EPermission.ProviderConfigTripCostManage],
  },
  {
    name: "Time & Day (Multiplier)",
    desc: "Configure cost multipliers depending on the time & day",
    to: "time-and-day",
    icon: "Clock",
    permissions: [EPermission.ProviderConfigPricingManage, EPermission.ProviderConfigTimeAndDayManage],
  },
  {
    name: "Hourly - Daily Hire",
    desc: "Hourly - Daily trip pricing configuration",
    to: "hourly-daily-hire",
    icon: "Timer1",
    permissions: [EPermission.ProviderConfigPricingManage, EPermission.ProviderConfigHourlyDailyHireManage],
  },
  {
    name: "Transfer & Vehicle Options",
    desc: "Transfer & Vehicle Options configurations",
    to: "vehicle-types",
    icon: "Car",
    permissions: [EPermission.ProviderConfigVehicleManage],
  },
  {
    name: "Unscheduled Stops",
    desc: "Configure unscheduled stops",
    to: "unscheduled-stops",
    icon: "Danger",
    permissions: [EPermission.ProviderConfigPricingManage, EPermission.ProviderConfigUnscheduledStopsManage],
  },
  {
    name: "Cancellation Fees",
    desc: "Configure cancellation policies",
    to: "cancellation-fees",
    icon: "MoneyRemove",
    permissions: [EPermission.ProviderConfigPricingManage, EPermission.ProviderConfigCancellationFeesManage],
  },
  {
    name: "Billing Settings",
    desc: "Billing configuration options",
    to: "billing-settings",
    icon: "ReceiptText",
    permissions: [EPermission.ProviderConfigProfileAdvancedManage, EPermission.ProviderConfigBillingSettingsManage],
  },
  {
    name: "Waiting Times",
    desc: "Set default airport and other waiting times",
    to: "waiting-times",
    icon: "Timer",
    permissions: [EPermission.ProviderConfigProfileAdvancedManage, EPermission.ProviderConfigWaitingTimesManage],
  },
];

export const TripPanel = () => {
  const { hasAllPermissions } = usePermissions();

  const visibleSubpages = subpages.filter(({ permissions }) => hasAllPermissions(permissions));

  return (
    <Panel>
      <Typography variant="h3">Trip Settings</Typography>
      <PageList className="-mx-5 mt-2">
        {visibleSubpages.map(({ name, desc, to, icon }) => (
          <PageList.Item key={name} title={name} description={desc} to={to} icon={icon} />
        ))}
      </PageList>
    </Panel>
  );
};

// @deprecated — assigned for the legacy paper trail only; no route reads this. Use TripCostV2.
TripPanel.TripCost = TripCost;
TripPanel.TripCostV2 = TripCostV2;
TripPanel.FixedCostExtras = FixedCostExtras;
TripPanel.VehicleTypes = VehicleTypes;
TripPanel.TimeAndDay = TimeAndDay;
TripPanel.TransferTypes = TransferTypes;
TripPanel.CancellationFees = CancellationFees;
TripPanel.UnscheduledStops = UnscheduledStops;
TripPanel.HourlyDailyHire = HourlyDailyHire;
TripPanel.BillingSettings = BillingSettings;
TripPanel.WaitingTimes = WaitingTimes;
TripPanel.DispatchPanel = DispatchPanel;
